import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { Modal, ModalType } from '@/components/Modal'
import { Icon } from '@/components/Icon'

interface Message {
  role: 'user' | 'assistant'
  content: string
  timestamp: string
}

interface ModalState {
  isOpen: boolean
  title: string
  message: string
  type: ModalType
  onConfirm?: () => void
  confirmText?: string
}

interface LeadField {
  key: string
  label: string
  category: string
}

// Lightweight Markdown formatter for assistant messages. Escapes HTML and
// converts bold, italic and line breaks to safe inline HTML.
function formatMarkdown(content: string): string {
  let html = content
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')

  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
  html = html.replace(/__(.+?)__/g, '<strong>$1</strong>')
  html = html.replace(/(^|[^*])\*(.+?)\*(?=[^*]|$)/g, '$1<em>$2</em>')
  html = html.replace(/(^|[^_])_(.+?)_(?=[^_]|$)/g, '$1<em>$2</em>')
  html = html.replace(/\n/g, '<br>')

  return html
}

export const ConversationDetail: React.FC = () => {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const queryClient = useQueryClient()
  
  const [modal, setModal] = useState<ModalState>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
  })

  const [showAllData, setShowAllData] = useState(false)

  const showModal = (title: string, message: string, type: ModalType = 'info', onConfirm?: () => void, confirmText?: string) => {
    setModal({ isOpen: true, title, message, type, onConfirm, confirmText })
  }

  const closeModal = () => {
    setModal(prev => ({ ...prev, isOpen: false }))
  }

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['conversation', id],
    queryFn: async () => {
      const response = await api.get(`/conversations/${id}`)
      return response.data.data
    },
  })

  // Mark conversation as viewed on open so the unread badge clears
  useEffect(() => {
    if (!id) return
    api.post(`/conversations/${id}/view`)
      .then(() => window.dispatchEvent(new Event('r3c:unread-count-refresh')))
      .catch((err) => console.error('Failed to mark conversation as viewed:', err))
  }, [id])

  const { data: leadFields } = useQuery({
    queryKey: ['lead-fields-config'],
    queryFn: async () => {
      const response = await api.get('/lead-fields/config')
      const fieldsData = response.data.data?.fields || response.data.data || []
      // Map snake_case API rows to the LeadField interface
      return fieldsData.map((f: any) => ({
        key: f.field_key,
        label: f.field_label,
        category: f.field_category,
      }))
    },
  })

  const updateStatusMutation = useMutation({
    mutationFn: async (status: string) => {
      const response = await api.put(`/conversations/${id}`, { status })
      return response.data
    },
    onSuccess: () => {
      refetch()
    },
    onError: (error: any) => {
      console.error('Failed to update status:', error)
      showModal(
        t('common.error'),
        error?.response?.data?.error || 'Failed to update status',
        'error'
      )
    },
  })

  const createLeadFromConversationMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/leads/from-conversation', { conversationId: id })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      refetch()
      showModal(
        t('common.success'),
        t('conversations.leadCreatedSuccess') || 'Lead created successfully from conversation',
        'success'
      )
    },
    onError: (error: any) => {
      console.error('Failed to create lead:', error)
      showModal(
        t('common.error'),
        error?.response?.data?.error || 'Failed to create lead from conversation',
        'error'
      )
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const response = await api.delete(`/conversations/${id}`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] })
      closeModal()
      navigate('/conversations')
    },
    onError: (error: any) => {
      console.error('Failed to delete:', error)
      showModal(
        t('common.error'),
        error?.response?.data?.error || 'Failed to delete conversation',
        'error'
      )
    },
  })

  const handleMarkAsConverted = () => {
    if (!data) return

    const extracted = data.extracted_data || {}
    const hasEmail =
      extracted.email ||
      extracted.correo ||
      extracted.contact_email ||
      data.visitor_email
    const hasPhone =
      extracted.phone ||
      extracted.telefono ||
      extracted.mobile ||
      extracted.contact_phone ||
      data.visitor_phone

    if (hasEmail || hasPhone) {
      // A contact method is available: create the lead and let the backend
      // mark the conversation as converted automatically.
      createLeadFromConversationMutation.mutate()
      return
    }

    if (Object.keys(extracted).length > 0) {
      // Some data exists but no usable contact method. Ask before marking as
      // converted without creating a lead.
      showModal(
        t('common.confirm'),
        t('conversations.noContactForLead') ||
          'No email or phone was found, so a lead cannot be created. Do you want to mark the conversation as converted anyway?',
        'confirm',
        () => updateStatusMutation.mutate('converted'),
        t('common.confirm')
      )
      return
    }

    // No usable data at all: mark as converted only after explicit confirmation.
    showModal(
      t('common.confirm'),
      t('conversations.noExtractedDataConfirm') ||
        'No contact data was found in this conversation, so no lead will be created. Mark as converted anyway?',
      'confirm',
      () => updateStatusMutation.mutate('converted'),
      t('common.confirm')
    )
  }

  const handleDeleteClick = () => {
    showModal(
      t('conversations.deleteTitle'),
      t('conversations.deleteConfirm'),
      'confirm',
      () => deleteMutation.mutate(),
      t('common.delete')
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">{t('common.loading')}</div>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">{t('common.noResults')}</div>
      </div>
    )
  }

  const conversation = data
  const messages: Message[] = Array.isArray(conversation.messages)
    ? conversation.messages
    : []
  const extractedData = conversation.extracted_data || {}

  const displayName =
    conversation.visitor_name ||
    extractedData.name ||
    extractedData.nombre ||
    extractedData.full_name ||
    extractedData.contact_name ||
    extractedData.first_name ||
    t('common.anonymous')

  const displayEmail =
    conversation.visitor_email ||
    extractedData.email ||
    extractedData.correo ||
    extractedData.contact_email ||
    t('common.noEmail')

  const displayPhone =
    conversation.visitor_phone ||
    extractedData.phone ||
    extractedData.telefono ||
    extractedData.mobile ||
    extractedData.contact_phone

  // Group extracted data by category
  const fieldsByCategory: Record<string, Array<{key: string, label: string, value: any}>> = {}
  
  if (leadFields && leadFields.length > 0) {
    // Use configured fields for grouping
    leadFields.forEach((field: LeadField) => {
      if (extractedData[field.key] !== undefined && extractedData[field.key] !== null) {
        const cat = field.category || 'general'
        if (!fieldsByCategory[cat]) fieldsByCategory[cat] = []
        fieldsByCategory[cat].push({
          key: field.key,
          label: field.label || field.key,
          value: extractedData[field.key]
        })
      }
    })
    // Add any extracted fields not in config
    Object.entries(extractedData).forEach(([key, value]) => {
      const found = leadFields.find((f: LeadField) => f.key === key)
      if (!found) {
        if (!fieldsByCategory['general']) fieldsByCategory['general'] = []
        fieldsByCategory['general'].push({
          key,
          label: key.replace(/_/g, ' '),
          value
        })
      }
    })
  } else {
    // Fallback: no config, show all flat
    Object.entries(extractedData).forEach(([key, value]) => {
      if (!fieldsByCategory['general']) fieldsByCategory['general'] = []
      fieldsByCategory['general'].push({
        key,
        label: key.replace(/_/g, ' '),
        value
      })
    })
  }

  const categoryOrder = ['contacto', 'perfil', 'producto', 'proyecto', 'equipo', 'logistica', 'pedido', 'servicio', 'comercial', 'general']
  const sortedCategories = Object.keys(fieldsByCategory).sort((a, b) => {
    const idxA = categoryOrder.indexOf(a)
    const idxB = categoryOrder.indexOf(b)
    if (idxA === -1 && idxB === -1) return a.localeCompare(b)
    if (idxA === -1) return 1
    if (idxB === -1) return -1
    return idxA - idxB
  })

  // Only show the expand/collapse button when at least one category has more than 5 fields
  const hasExpandableData = sortedCategories.some((category) => (fieldsByCategory[category]?.length || 0) > 5)

  const categoryIcons: Record<string, string> = {
    contacto: 'user',
    perfil: 'id-card',
    producto: 'box',
    proyecto: 'hammer',
    equipo: 'truck',
    logistica: 'truck-fast',
    pedido: 'cart-shopping',
    servicio: 'briefcase',
    comercial: 'handshake',
    general: 'circle-info',
  }

  const categoryLabels: Record<string, string> = {
    contacto: t('leadFields.cat_contact') || 'Contact',
    perfil: t('leadFields.cat_profile') || 'Profile',
    producto: t('leadFields.cat_product') || 'Product',
    proyecto: t('leadFields.cat_project') || 'Project',
    equipo: t('leadFields.cat_equipment') || 'Equipment',
    logistica: t('leadFields.cat_logistics') || 'Logistics',
    pedido: t('leadFields.cat_order') || 'Order',
    servicio: t('leadFields.cat_service') || 'Service',
    comercial: t('leadFields.cat_commercial') || 'Commercial',
    general: t('leadFields.cat_general') || 'General',
  }

  return (
    <div>
      {/* Reusable Modal */}
      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        onConfirm={modal.onConfirm}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        confirmText={modal.confirmText}
        cancelText={t('common.cancel')}
        isLoading={deleteMutation.isPending || createLeadFromConversationMutation.isPending}
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <button
            onClick={() => navigate('/conversations')}
            className="text-sm text-gray-400 hover:text-white mb-2 flex items-center gap-1"
          >
            <Icon name="arrow-left" className="w-3" />
            {t('conversations.backToConversations')}
          </button>

          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">
              {displayName}
            </h1>
            <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium capitalize ${
              conversation.status === 'active' ? 'bg-green-500/20 text-green-400' :
              conversation.status === 'converted' ? 'bg-blue-500/20 text-blue-400' :
              conversation.status === 'closed' ? 'bg-gray-500/20 text-gray-400' :
              'bg-red-500/20 text-red-400'
            }`}>
              {conversation.status}
            </span>
          </div>
          <p className="text-gray-400 text-sm mt-1">
            {displayEmail}
            {displayPhone && ` · ${displayPhone}`}
          </p>
        </div>
        <div className="flex gap-2">
          {conversation.status !== 'converted' && !conversation.lead_id && (
            <button
              onClick={handleMarkAsConverted}
              disabled={updateStatusMutation.isPending || createLeadFromConversationMutation.isPending}
              className="px-4 py-2 bg-green-500/10 text-green-400 border border-green-500/20 rounded-lg hover:bg-green-500/20 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              <Icon name="check" className="w-4" />
              {createLeadFromConversationMutation.isPending 
                ? t('common.creatingLead') 
                : t('conversations.markConverted')}
            </button>
          )}
          {conversation.status !== 'closed' && (
            <button
              onClick={() => updateStatusMutation.mutate('closed')}
              disabled={updateStatusMutation.isPending}
              className="px-4 py-2 bg-white/5 text-gray-300 border border-white/10 rounded-lg hover:bg-white/10 transition-colors disabled:opacity-50"
            >
              {t('conversations.close')}
            </button>
          )}
          <button
            onClick={handleDeleteClick}
            disabled={deleteMutation.isPending}
            className="px-4 py-2 bg-red-500/10 text-red-400 border border-red-500/20 rounded-lg hover:bg-red-500/20 transition-colors disabled:opacity-50"
          >
            <Icon name="trash" className="w-4" />
          </button>
        </div>
      </div>

      {/* Main Layout: Chat + Info */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Chat Messages */}
        <div className="lg:col-span-2">
          <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Icon name="comments" className="w-5 text-[#006cd4]" />
              {t('conversations.detailTitle')}
            </h2>
            <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2">
              {messages.length === 0 ? (
                <div className="text-gray-400 text-center py-8">{t('conversations.noMessages')}</div>
              ) : (
                messages.map((msg, index) => (
                  <div
                    key={index}
                    className={`flex ${msg.role === 'user' ? 'justify-start' : 'justify-end'}`}
                  >
                    <div
                      className={`max-w-[80%] px-4 py-3 rounded-xl ${
                        msg.role === 'user'
                          ? 'bg-[#1a1a25] text-white border border-white/5'
                          : 'bg-[#006cd4]/20 text-white border border-[#006cd4]/20'
                      }`}
                    >
                      <div
                        className="text-sm whitespace-pre-wrap"
                        dangerouslySetInnerHTML={{ __html: formatMarkdown(msg.content.replace('[OPEN_LEAD_FORM]', '').trim()) }}
                      />
                      <div className="text-xs text-gray-500 mt-1">
                        {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : ''}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Right: Quick Info Sidebar */}
        <div className="space-y-4">
          {/* Lead Link */}
          {conversation.lead_id && (
            <div className="bg-[#12121a] border border-white/10 rounded-xl p-4">
              <button
                onClick={() => navigate(`/leads/${conversation.lead_id}`)}
                className="w-full px-4 py-2 bg-[#006cd4]/20 text-[#006cd4] rounded-lg hover:bg-[#006cd4]/30 transition-colors flex items-center justify-center gap-2"
              >
                <Icon name="arrow-up-right-from-square" className="w-4" />
                {t('conversations.viewLead')}
              </button>
            </div>
          )}

          {/* Visitor Info */}
          <div className="bg-[#12121a] border border-white/10 rounded-xl p-4">
            <h3 className="text-sm font-medium text-gray-400 mb-3">{t('conversations.visitorInfo')}</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">{t('conversations.source')}</span>
                <span className="text-white capitalize">{conversation.source || 'widget'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">{t('conversations.started')}</span>
                <span className="text-white">{new Date(conversation.created_at).toLocaleDateString()}</span>
              </div>
              {conversation.ip_address && (
                <div className="flex justify-between">
                  <span className="text-gray-500">IP</span>
                  <span className="text-white font-mono text-xs">{conversation.ip_address}</span>
                </div>
              )}
              {conversation.country && (
                <div className="flex justify-between">
                  <span className="text-gray-500">{t('conversations.country')}</span>
                  <span className="text-white">{conversation.country}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Extracted Data Section - Full Width */}
      {Object.keys(extractedData).length > 0 && (
        <div className="mt-6">
          <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Icon name="clipboard-list" className="w-5 text-[#006cd4]" />
                {t('conversations.extractedData') || 'Extracted Data'}
                <span className="text-sm font-normal text-gray-400">
                  ({Object.keys(extractedData).length} {t('common.entries') || 'fields'})
                </span>
              </h2>
              {hasExpandableData && (
                <button
                  onClick={() => setShowAllData(!showAllData)}
                  className="text-sm text-[#006cd4] hover:text-[#0052a3] flex items-center gap-1"
                >
                  {showAllData ? t('common.collapse') || 'Collapse' : t('common.expand') || 'Expand'}
                  <Icon name={showAllData ? 'chevron-up' : 'chevron-down'} className="w-3" />
                </button>
              )}
            </div>

            {/* Summary Row - Always visible at top */}
            <div className="mb-6 pb-6 border-b border-white/5 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {Object.entries(extractedData).slice(0, 6).map(([key, value]) => (
                <div key={key} className="bg-[#0a0a0f] rounded-lg p-3 border border-white/5">
                  <div className="text-xs text-gray-500 capitalize mb-1 truncate">{key.replace(/_/g, ' ')}</div>
                  <div className="text-sm text-white font-medium truncate">{String(value) || '-'}</div>
                </div>
              ))}
            </div>

            {/* Data Cards by Category - Full width grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-start">
              {sortedCategories.map((category) => {
                const fields = fieldsByCategory[category]
                if (!fields || fields.length === 0) return null
                
                // Show all fields when expanded, first 5 when collapsed
                const displayFields = showAllData ? fields : fields.slice(0, 5)
                const hasMore = fields.length > 5

                return (
                  <div key={category} className={`bg-[#0a0a0f] border border-white/5 rounded-lg p-4 ${sortedCategories.length < 4 ? 'lg:col-span-2' : ''} ${sortedCategories.length === 1 ? 'sm:col-span-2 lg:col-span-4' : ''} ${sortedCategories.length === 2 ? 'sm:col-span-2 lg:col-span-2' : ''}`}>
                    <div className="flex items-center gap-2 mb-3 pb-2 border-b border-white/5">
                      <Icon 
                        name={categoryIcons[category] || 'circle'} 
                        className="w-4 text-[#006cd4]" 
                      />
                      <h3 className="font-medium text-white text-sm">
                        {categoryLabels[category] || category}
                      </h3>
                      <span className="text-xs text-gray-500 ml-auto">{fields.length}</span>
                    </div>
                    <div className="space-y-2">
                      {displayFields.map((field) => (
                        <div key={field.key} className="group">
                          <div className="text-xs text-gray-500 mb-0.5">{field.label}</div>
                          <div className="text-sm text-white break-words">
                            {String(field.value) || 
                              <span className="text-gray-600 italic text-xs">{t('common.notSet') || 'Not set'}</span>
                            }
                          </div>
                        </div>
                      ))}
                      {hasMore && !showAllData && (
                        <div className="text-xs text-gray-500 italic pt-1">
                          +{fields.length - 5} more
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
